/**
 * Minimal view router for the dashboard shell.
 *
 * The app is a single-page surface: the shell owns a persistent chrome
 * (sidebar + header) and a single content host. Navigating swaps the active
 * {@link View} — unmounting the previous one (so it can tear down store/WS
 * subscriptions and timers) before mounting the next.
 *
 * Views are created lazily via a {@link ViewFactory}, keeping the initial
 * bundle small: a section's code only loads the first time it's opened.
 * @module app/router
 */
import { renderError, renderLoading } from "../core/ui.ts";

/**
 * A mountable screen. `mount` renders into the supplied host and wires up any
 * subscriptions; `unmount` (optional) must release them to avoid leaks.
 */
export interface View {
	mount(host: HTMLElement): void | Promise<void>;
	unmount?(): void;
}

/** Lazily produces a view instance for a route. */
export type ViewFactory = () => View | Promise<View>;

/** Swaps views in and out of a single content host. */
export class Router {
	private current: View | null = null;
	private currentRoute: string | null = null;
	private token = 0;

	constructor(
		private readonly host: HTMLElement,
		private readonly routes: Map<string, ViewFactory>,
		private readonly onRouteChange?: (routeId: string) => void,
	) {}

	/** Update the active routes map dynamically (e.g. when customizations are loaded). */
	updateRoutes(newRoutes: Map<string, ViewFactory>): void {
		this.routes.clear();
		for (const [k, v] of newRoutes) {
			this.routes.set(k, v);
		}
	}

	/** The currently active route id, or null before the first navigation. */
	get active(): string | null {
		return this.currentRoute;
	}

	/** Navigate to a route. No-op if already active (unless `force`). */
	async navigate(routeId: string, force = false): Promise<void> {
		if (!force && routeId === this.currentRoute) return;
		const factory = this.routes.get(routeId);
		if (!factory) {
			renderError(this.host, `Unknown view: ${routeId}`);
			return;
		}

		// Invalidate any in-flight navigation that hasn't finished mounting yet.
		const myToken = ++this.token;

		try {
			this.current?.unmount?.();
		} catch (err) {
			console.error("view unmount failed", err);
		}
		this.current = null;
		this.currentRoute = routeId;
		this.onRouteChange?.(routeId);

		renderLoading(this.host, "Loading…");

		let view: View;
		try {
			view = await factory();
		} catch (err) {
			if (myToken !== this.token) return; // superseded
			renderError(
				this.host,
				`Failed to load view: ${(err as Error).message}`,
				() => this.navigate(routeId, true),
			);
			return;
		}

		if (myToken !== this.token) return; // a newer navigation won

		this.host.innerHTML = "";
		this.current = view;
		try {
			await view.mount(this.host);
		} catch (err) {
			if (myToken !== this.token) return;
			renderError(
				this.host,
				`Failed to render view: ${(err as Error).message}`,
				() => this.navigate(routeId, true),
			);
		}
	}
}
